import { Email, Event, Info, Lock, Person } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useLocation, useNavigate } from "react-router-dom";
import OperateurEcoIcon from "../../../asset/images/OperateurEcoIcon.png";
import SecteurAIcon from "../../../asset/images/SecteurAIcon.png";
import worldIcon from "../../../asset/images/worldIcon.png";
import { AuthContext } from "../../../common/components/general/TSAuthProvider";
import { useTSData } from "../../../common/components/general/TSDataContext";
import AppSelectInputApi from "../../../common/components/ui/AppSelectInputApi";
import TSGrid from "../../../common/components/ui/TSGrid";
import { MESSAGING_MODE } from "../../../common/constants/generalConstants";
import { MESSAGING_PAGE_ROUTE } from "../../../common/constants/routerConstants";
import config from "../../../config";

const RegisterPage = () => {
  const STRONG_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

  
  const { register, setOpenLoginDialog } = useContext(AuthContext);
  const { setMode: setModeApp } = useTSData();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const emailFromState = location.state?.email || "";

  const [registerData, setRegisterData] = useState({
    email: emailFromState,
    password: "", confirmPassword: "", fullName: "", birthDate: "",
    confirmationCode: "", paysId: "", regionId: "", sectorId: "", typeOperatorId: ""
  });

  const [registerErrors, setRegisterErrors] = useState({
    email: "", password: "", confirmPassword: "", fullName: "", birthDate: "",
    confirmationCode: "", pays: "", region: "", sector: "", typeOperator: ""
  });

  const [passwordStrength, setPasswordStrength]           = useState({ score: 0, feedback: "" });
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [isConfirmingEmail, setIsConfirmingEmail]         = useState(false);
  const [resendCooldown, setResendCooldown]               = useState(0);
  const [selectedPays, setSelectedPays]                   = useState(null);
  const [selectedRegion, setSelectedRegion]               = useState(null);
  const [selectedSector, setSelectedSector]               = useState(null);
  const [selectedOperator, setSelectedOperator]           = useState(null);
  const [isRegistering, setIsRegistering]                 = useState(false);

  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
    setCaptchaToken(null);
    setCaptchaError(false);
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const calculatePasswordStrength = (password) => {
    const criteria = [
      { test: /.{8,}/.test(password),        label: "au moins 8 caractères" },
      { test: /[a-z]/.test(password),         label: "une lettre minuscule" },
      { test: /[A-Z]/.test(password),         label: "une lettre majuscule" },
      { test: /\d/.test(password),            label: "un chiffre" },
      { test: /[^a-zA-Z0-9]/.test(password), label: "un caractère spécial" },
    ];
    const score   = criteria.filter(c => c.test).length;
    const missing = criteria.filter(c => !c.test).map(c => c.label);
    const levels  = ["Très faible", "Faible", "Moyen", "Fort", "Valide ✓"];
    return {
      score,
      level:    levels[Math.min(score, 4)],
      feedback: missing.length > 0 ? `Manque: ${missing.join(", ")}` : "✅ Mot de passe valide",
      isValid:  STRONG_PASSWORD_PATTERN.test(password),
    };
  };

  const handleEmailConfirmation = async (isResend = false) => {
    if (!validateEmail(registerData.email)) {
      setRegisterErrors({ ...registerErrors, email: "Format d'email invalide" });
      return;
    }
    if (!captchaToken) { setCaptchaError(true); return; }

    setIsConfirmingEmail(true);
    try {
      const response = await fetch(`${config.baseURL}api/user/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registerData.email, captchaToken }),
      });
      if (response.ok) {
        setEmailConfirmationSent(true);
        setResendCooldown(60);
        resetCaptcha();
        setRegisterErrors({ ...registerErrors, email: "", confirmationCode: "" });
        if (isResend) setRegisterData({ ...registerData, confirmationCode: "" });
      } else {
        resetCaptcha();
        setRegisterErrors({ ...registerErrors, email: "Erreur lors de l'envoi du code de confirmation" });
      }
    } catch {
      resetCaptcha();
      setRegisterErrors({ ...registerErrors, email: "Erreur de connexion au serveur" });
    }
    setIsConfirmingEmail(false);
  };

  const validateRegistrationData = () => {
    const errors = {};
    if (!registerData.email)                       errors.email = "L'email est requis";
    else if (!validateEmail(registerData.email))   errors.email = "Format d'email invalide";
    if (!registerData.password)                    errors.password = "Le mot de passe est requis";
    else if (registerData.password.length < 8)     errors.password = "Le mot de passe doit contenir au moins 8 caractères";
    else if (passwordStrength.score < 3)           errors.password = "Le mot de passe n'est pas assez fort";
    if (!registerData.confirmPassword)             errors.confirmPassword = "La confirmation du mot de passe est requise";
    else if (registerData.password !== registerData.confirmPassword) errors.confirmPassword = "Les mots de passe ne correspondent pas";
    if (!registerData.fullName)                    errors.fullName = "Le nom complet est requis";
    if (!registerData.birthDate)                   errors.birthDate = "La date de naissance est requise";

    if (!registerData.paysId) {
      errors.pays = "Le pays est requis";
    }

    if (selectedPays?.label === "Maroc" && !registerData.regionId) {
      errors.region = "La région est requise";
    }

    if (!registerData.sectorId) {
      errors.sector = "Le secteur d'activité est requis";
    }

    if (!registerData.typeOperatorId) {
      errors.typeOperator = "Le type d'opérateur est requis";
    }
    if (emailConfirmationSent && !registerData.confirmationCode)
      errors.confirmationCode = "Veuillez saisir le code de confirmation reçu par email";
    return errors;
  };

  const handleRegistration = async () => {
    const errors = validateRegistrationData();
    setRegisterErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (emailConfirmationSent && !registerData.confirmationCode) {
      setRegisterErrors({ ...registerErrors, confirmationCode: "⚠️ Veuillez saisir le code de confirmation reçu par email pour finaliser votre inscription" });
      return;
    }

    setIsRegistering(true);
    register({
      email:          registerData.email,
      password:       registerData.password,
      fullName:       registerData.fullName,
      birthDate:      registerData.birthDate,
      paysId:         registerData.paysId,
      sectorId:       registerData.sectorId,
      typeOperatorId: registerData.typeOperatorId,
      regionId:       -1,
      ...(selectedPays?.label === "Maroc" && { regionId: registerData.regionId }),
      ...(emailConfirmationSent && { confirmationCode: registerData.confirmationCode }),
      onSuccess: () => {
        setIsRegistering(false);
        setModeApp(MESSAGING_MODE);
        navigate(MESSAGING_PAGE_ROUTE);
      },
      onFail: (errorMessage) => {
        setIsRegistering(false);
        setRegisterErrors({ ...registerErrors, email: errorMessage || "Erreur lors de l'inscription" });
      },
    });
  };

  const handleRegisterInputChange = (field, value) => {
    setRegisterData({ ...registerData, [field]: value });
    if (field === "password") setPasswordStrength(calculatePasswordStrength(value));
    if (registerErrors[field]) setRegisterErrors({ ...registerErrors, [field]: "" });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "20px", padding: "40px", backgroundColor: "white", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
        <Typography variant="h4" sx={{ fontSize: "30px", lineHeight: "45px", color: "#1B738A", textAlign: "center", mb: 2 }}>
          Créer un compte
        </Typography>
        <Typography variant="body1" sx={{ textAlign: "center", color: "gray", mb: 4 }}>
          Rejoignez-nous pour obtenir des réponses plus intelligentes
        </Typography>

        {isMobile ? (
          <TSGrid container spacing={3}>
            {/* ── MOBILE FIELDS ── */}
            <TSGrid item xs={12}>
              <TextField fullWidth label="Nom complet" type="text" value={registerData.fullName}
                onChange={(e) => handleRegisterInputChange("fullName", e.target.value)}
                error={!!registerErrors.fullName} helperText={registerErrors.fullName}
                InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: "gray" }} /></InputAdornment> }} />
            </TSGrid>
            <TSGrid item xs={12}>
              <TextField fullWidth label="Email" type="email" value={registerData.email}
                onChange={(e) => handleRegisterInputChange("email", e.target.value)}
                error={!!registerErrors.email}
                helperText={registerErrors.email || (emailConfirmationSent
                  ? <Typography variant="caption" sx={{ color: "#4caf50" }}>✅ Code envoyé !</Typography>
                  : registerData.email && validateEmail(registerData.email) && <Typography variant="caption" sx={{ color: "#1B738A" }}>📧 Cliquez sur "Confirmer l'email"</Typography>)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "gray" }} /></InputAdornment> }} />
            </TSGrid>
            <TSGrid item xs={12}>
              <TextField fullWidth label="Mot de passe" type="password" value={registerData.password}
                onChange={(e) => handleRegisterInputChange("password", e.target.value)}
                error={!!registerErrors.password} helperText={registerErrors.password}
                InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "gray" }} /></InputAdornment> }} />
              {registerData.password && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="caption" sx={{ color: "gray", fontSize: "12px" }}>
                    Force : <span style={{ color: passwordStrength.score < 2 ? "#f44336" : passwordStrength.score < 4 ? "#ff9800" : "#4caf50", fontWeight: "bold" }}>{passwordStrength.level}</span>
                  </Typography>
                  <Box sx={{ display: "flex", mt: 0.5 }}>
                    {[1,2,3,4,5].map(l => <Box key={l} sx={{ width: "20%", height: "4px", mr: 0.5, borderRadius: "2px",
                      backgroundColor: l <= passwordStrength.score ? (passwordStrength.score < 2 ? "#f44336" : passwordStrength.score < 4 ? "#ff9800" : "#4caf50") : "#e0e0e0" }} />)}
                  </Box>
                  <Typography variant="caption" sx={{ color: "gray", fontSize: "10px" }}>{passwordStrength.feedback}</Typography>
                </Box>
              )}
            </TSGrid>
            <TSGrid item xs={12}>
              <TextField fullWidth label="Confirmer le mot de passe" type="password" value={registerData.confirmPassword}
                onChange={(e) => handleRegisterInputChange("confirmPassword", e.target.value)}
                error={!!registerErrors.confirmPassword} helperText={registerErrors.confirmPassword}
                InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "gray" }} /></InputAdornment> }} />
            </TSGrid>
            <TSGrid item xs={12}>
              <TextField fullWidth label="Date de naissance" type="date" value={registerData.birthDate}
                onChange={(e) => handleRegisterInputChange("birthDate", e.target.value)}
                error={!!registerErrors.birthDate} helperText={registerErrors.birthDate}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Event sx={{ color: "gray" }} /></InputAdornment> }} />
            </TSGrid>
            <TSGrid item xs={12}>
              <AppSelectInputApi name="pays" label="Pays" value={selectedPays}
                onChange={(e) => { setSelectedPays(e); setRegisterData({ ...registerData, paysId: e?.id || "", ...(e?.label !== "Maroc" && { regionId: "" }) }); if (e?.label !== "Maroc") setSelectedRegion(null); }}
                apiOptions={{ api: "api/referential/pays" }}
                startAdornment={<img src={worldIcon} alt="world" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                error={registerErrors.pays} />
            </TSGrid>
            {selectedPays?.label === "Maroc" && (
              <TSGrid item xs={12}>
                <AppSelectInputApi name="region" label="Région" value={selectedRegion}
                  onChange={(e) => { setSelectedRegion(e); setRegisterData({ ...registerData, regionId: e?.id || "" }); }}
                  apiOptions={{ api: `api/referential/region?v0=${selectedPays.id}` }}
                  startAdornment={<img src={worldIcon} alt="region" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                  error={registerErrors.region} />
              </TSGrid>
            )}
            <TSGrid item xs={12}>
              <AppSelectInputApi name="sectorId" label="Secteur d'activité" value={selectedSector}
                onChange={(e) => { setSelectedSector(e); setRegisterData({ ...registerData, sectorId: e?.id || "" }); }}
                apiOptions={{ api: "api/referential/secteur-activite" }}
                startAdornment={<img src={SecteurAIcon} alt="sector" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                error={registerErrors.sector} />
            </TSGrid>
            <TSGrid item xs={12}>
              <AppSelectInputApi name="typeOperatorId" label="Type d'opérateur économique" value={selectedOperator}
                onChange={(e) => { setSelectedOperator(e); setRegisterData({ ...registerData, typeOperatorId: e?.id || "" }); }}
                apiOptions={{ api: "api/referential/type-operateur" }}
                startAdornment={<img src={OperateurEcoIcon} alt="operator" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                error={registerErrors.typeOperator} />
            </TSGrid>
          </TSGrid>
        ) : (
          <TSGrid container spacing={4}>
            {/* ── DESKTOP: Left Block ── */}
            <TSGrid item xs={12} md={6}>
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "15px", padding: "24px", backgroundColor: "#f9f9f9" }}>
                <Typography variant="h6" sx={{ mb: 3, color: "#1B738A", fontWeight: "bold" }}>Informations du compte</Typography>
                <TSGrid container spacing={3}>
                  <TSGrid item xs={12}>
                    <TextField fullWidth label="Nom complet" type="text" value={registerData.fullName}
                      onChange={(e) => handleRegisterInputChange("fullName", e.target.value)}
                      error={!!registerErrors.fullName} helperText={registerErrors.fullName}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: "gray" }} /></InputAdornment> }} />
                  </TSGrid>
                  <TSGrid item xs={12}>
                    <TextField fullWidth label="Email" type="email" value={registerData.email}
                      onChange={(e) => handleRegisterInputChange("email", e.target.value)}
                      error={!!registerErrors.email}
                      helperText={registerErrors.email || (emailConfirmationSent
                        ? <Typography variant="caption" sx={{ color: "#4caf50" }}>✅ Code envoyé ! Vérifiez votre email puis saisissez le code ci-dessous</Typography>
                        : registerData.email && validateEmail(registerData.email) && <Typography variant="caption" sx={{ color: "#1B738A" }}>📧 Cliquez sur "Confirmer l'email" pour recevoir un code de vérification</Typography>)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "gray" }} /></InputAdornment> }} />
                  </TSGrid>
                  <TSGrid item xs={12}>
                    <TextField fullWidth label="Mot de passe" type="password" value={registerData.password}
                      onChange={(e) => handleRegisterInputChange("password", e.target.value)}
                      error={!!registerErrors.password} helperText={registerErrors.password}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "gray" }} /></InputAdornment> }} />
                    {registerData.password && (
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="caption" sx={{ color: "gray", fontSize: "12px" }}>
                          Force du mot de passe :&nbsp;
                          <span style={{ color: passwordStrength.score < 2 ? "#f44336" : passwordStrength.score < 4 ? "#ff9800" : "#4caf50", fontWeight: "bold" }}>{passwordStrength.level}</span>
                        </Typography>
                        <Box sx={{ display: "flex", mt: 0.5 }}>
                          {[1,2,3,4,5].map(l => <Box key={l} sx={{ width: "20%", height: "4px", mr: 0.5, borderRadius: "2px",
                            backgroundColor: l <= passwordStrength.score ? (passwordStrength.score < 2 ? "#f44336" : passwordStrength.score < 4 ? "#ff9800" : "#4caf50") : "#e0e0e0" }} />)}
                        </Box>
                        <Typography variant="caption" sx={{ color: "gray", fontSize: "10px" }}>{passwordStrength.feedback}</Typography>
                      </Box>
                    )}
                  </TSGrid>
                  <TSGrid item xs={12}>
                    <TextField fullWidth label="Confirmer le mot de passe" type="password" value={registerData.confirmPassword}
                      onChange={(e) => handleRegisterInputChange("confirmPassword", e.target.value)}
                      error={!!registerErrors.confirmPassword} helperText={registerErrors.confirmPassword}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "gray" }} /></InputAdornment> }} />
                  </TSGrid>
                  <TSGrid item xs={12}>
                    <TextField fullWidth label="Date de naissance" type="date" value={registerData.birthDate}
                      onChange={(e) => handleRegisterInputChange("birthDate", e.target.value)}
                      error={!!registerErrors.birthDate} helperText={registerErrors.birthDate}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Event sx={{ color: "gray" }} /></InputAdornment> }} />
                  </TSGrid>
                </TSGrid>
              </Box>
            </TSGrid>

            {/* ── DESKTOP: Right Block ── */}
            <TSGrid item xs={12} md={6}>
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "15px", padding: "24px", backgroundColor: "#f9f9f9" }}>
                <Typography variant="h6" sx={{ mb: 3, color: "#1B738A", fontWeight: "bold" }}>Informations sur l'opérateur</Typography>
                <TSGrid container spacing={3}>
                  <TSGrid item xs={12}>
                    <AppSelectInputApi name="pays" label="Pays" value={selectedPays}
                      onChange={(e) => { setSelectedPays(e); setRegisterData({ ...registerData, paysId: e?.id || "", ...(e?.label !== "Maroc" && { regionId: "" }) }); if (e?.label !== "Maroc") setSelectedRegion(null); }}
                      apiOptions={{ api: "api/referential/pays" }}
                      startAdornment={<img src={worldIcon} alt="world" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                      error={registerErrors.pays} />
                  </TSGrid>
                  {selectedPays?.label === "Maroc" && (
                    <TSGrid item xs={12}>
                      <AppSelectInputApi name="region" label="Région" value={selectedRegion}
                        onChange={(e) => { setSelectedRegion(e); setRegisterData({ ...registerData, regionId: e?.id || "" }); }}
                        apiOptions={{ api: `api/referential/region?v0=${selectedPays.id}` }}
                        startAdornment={<img src={worldIcon} alt="region" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                        error={registerErrors.region} />
                    </TSGrid>
                  )}
                  <TSGrid item xs={12}>
                    <AppSelectInputApi name="sectorId" label="Secteur d'activité" value={selectedSector}
                      onChange={(e) => { setSelectedSector(e); setRegisterData({ ...registerData, sectorId: e?.id || "" }); }}
                      apiOptions={{ api: "api/referential/secteur-activite" }}
                      startAdornment={<img src={SecteurAIcon} alt="sector" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                      error={registerErrors.sector} />
                  </TSGrid>
                  <TSGrid item xs={12}>
                    <AppSelectInputApi name="typeOperatorId" label="Type d'opérateur économique" value={selectedOperator}
                      onChange={(e) => { setSelectedOperator(e); setRegisterData({ ...registerData, typeOperatorId: e?.id || "" }); }}
                      apiOptions={{ api: "api/referential/type-operateur" }}
                      startAdornment={<img src={OperateurEcoIcon} alt="operator" style={{ width: 20, height: 20, paddingRight: 5 }} />}
                      error={registerErrors.typeOperator} />
                  </TSGrid>
                </TSGrid>
              </Box>
            </TSGrid>
          </TSGrid>
        )}

        <TSGrid container spacing={3} sx={{ mt: 3 }}>
          {/* reCAPTCHA */}
          {registerData.email && validateEmail(registerData.email) && !emailConfirmationSent && (
            <TSGrid item xs={12} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <ReCAPTCHA ref={recaptchaRef} sitekey={config.recaptchaSiteKey}
                onChange={(v) => { setCaptchaToken(v); setCaptchaError(false); }}
                onExpired={() => setCaptchaToken(null)} />
              {captchaError && <Typography variant="caption" sx={{ color: "#f44336", mt: 0.5 }}>Veuillez confirmer que vous n'êtes pas un robot</Typography>}
            </TSGrid>
          )}

          {/* Confirmation code */}
          {emailConfirmationSent && (
            <TSGrid item xs={12}>
              <TextField fullWidth
                label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Code de confirmation
                  <Tooltip title="💡 Code de 6 chiffres envoyé par email. Vérifiez aussi le dossier spam. Le code expire dans 10 minutes." arrow placement="top">
                    <IconButton size="small" sx={{ padding: 0, ml: 0.5 }}><Info sx={{ fontSize: "16px", color: "#1B738A" }} /></IconButton>
                  </Tooltip>
                </Box>}
                type="text" value={registerData.confirmationCode}
                onChange={(e) => handleRegisterInputChange("confirmationCode", e.target.value)}
                error={!!registerErrors.confirmationCode}
                helperText={registerErrors.confirmationCode || (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "gray" }}>📬 Code de 6 chiffres reçu par email (expire dans 10 min)</Typography>
                    <Button size="small" variant="text" onClick={() => handleEmailConfirmation(true)}
                      disabled={isConfirmingEmail || resendCooldown > 0 || isRegistering}
                      sx={{ minWidth: "auto", padding: "2px 8px", fontSize: "10px", color: resendCooldown > 0 ? "gray" : "#1B738A", textTransform: "none" }}>
                      {isConfirmingEmail ? "..." : resendCooldown > 0 ? `Renvoyer (${resendCooldown}s)` : "Renvoyer le code"}
                    </Button>
                  </Box>
                )}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Typography sx={{ color: "#1B738A", fontSize: "16px", fontWeight: "bold" }}>🔐</Typography></InputAdornment>,
                  style: { backgroundColor: "#f8f9ff", borderRadius: "8px" }
                }} />
            </TSGrid>
          )}

          {/* Submit */}
          <TSGrid item xs={12}>
            <Button fullWidth variant="contained"
              onClick={() => {
                if (registerData.email && validateEmail(registerData.email) && !emailConfirmationSent) {
                  handleEmailConfirmation();
                } else {
                  handleRegistration();
                }
              }}
              disabled={isConfirmingEmail || isRegistering}
              sx={{ backgroundColor: "#1B738A", "&:hover": { backgroundColor: "#155f73" }, "&:disabled": { backgroundColor: "#cccccc" }, borderRadius: "10px", padding: "12px", mt: 2 }}>
              {isConfirmingEmail
                ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={20} sx={{ color: "white" }} />Envoi du code...</Box>
                : isRegistering
                  ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={20} sx={{ color: "white" }} />Inscription...</Box>
                  : (registerData.email && validateEmail(registerData.email) && !emailConfirmationSent)
                    ? "📧 Confirmer l'email"
                    : "S'inscrire"}
            </Button>
          </TSGrid>

          <TSGrid item xs={12} sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "gray" }}>
              Déjà un compte ?{" "}
              <Button variant="text"
                onClick={() => { navigate("/"); setTimeout(() => setOpenLoginDialog(true), 100); }}
                sx={{ color: "#1B738A", textDecoration: "underline", textTransform: "none" }}>
                Se connecter
              </Button>
            </Typography>
          </TSGrid>
        </TSGrid>
      </Box>
    </Container>
  );
};

export default RegisterPage;
